import React, { useEffect, useState, useCallback } from "react";
import {fetchWrongQuestions} from "../../../utils/growth";
import { Button, Collapse } from "react-bootstrap";
import {
  ChevronUp,
  ChevronDown,
  CheckCircleFill,
  ArrowClockwise,
} from "react-bootstrap-icons";
import "./styles.scss";
import {WrongQuestionRow} from "./Wrongquestionsshared";

const INK = "#1F2430";
const MUTED = "#6B7280";
const CARD = "#FFFFFF";
const HAIRLINE = "#EBEAF2";
const BRAND = "#6C5DD3";
const BRAND_SOFT = "#F1EEFE";
const CORRECT = "#2F9E63";
const BODY_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

function SkeletonRow() {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 4px" }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: "#EFEFF4", flexShrink: 0 }} className="rmw-pulse" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ height: 12, width: "70%", borderRadius: 6, background: "#EFEFF4", marginBottom: 8 }} className="rmw-pulse" />
        <div style={{ height: 10, width: "40%", borderRadius: 6, background: "#EFEFF4" }} className="rmw-pulse" />
      </div>
    </div>
  );
}

function RecentMistakesWidget({
  limit = 5,
  onViewAll,
  defaultCollapsed = false,
  studentId,
  classroomId,
  isMobileView = false,
  isProgressPage = false,
  subjectsMap = {},
  hasIeltsExams = false
}) {
  const [state, setState] = useState({ status: "loading", items: [], total: 0 });
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [openRows, setOpenRows] = useState(() => new Set());

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const res = await fetchWrongQuestions(classroomId, studentId, { page: 1, limit, hasIeltsExams });
      setState({
        status: "ready",
        items: res?.wrongAnswers || [],
        total: res?.pagination?.total ?? (res?.wrongAnswers || []).length,
      });
    } catch (err) {
      setState({ status: "error", items: [], total: 0 });
    }
  }, [classroomId, studentId, limit, hasIeltsExams]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRow = (key) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const hasMore = state.total > state.items.length;
  const WidgetChevron = collapsed ? ChevronDown : ChevronUp;

  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${isProgressPage ? HAIRLINE : 'rgba(0,0,0,0.175)'}`,
        borderRadius: isProgressPage ? '16px' : 0,
        padding: "18px 20px 8px",
        fontFamily: BODY_FONT,
        boxSizing: "border-box",
      }}
    >
      <style>{`
        @keyframes rmwPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
        .rmw-pulse { animation: rmwPulse 1.3s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: collapsed ? 0 : 4 }}>
        <div>
          <h5>Recent Mistakes</h5>
          {!collapsed && (
            <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
              Review what you got wrong to strengthen weak areas
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {!collapsed && state.status === "ready" && hasMore && (
            <Button
              variant="link"
              onClick={onViewAll}
              style={{
                color: BRAND,
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
                padding: "4px 8px",
                whiteSpace: "nowrap",
              }}
            >
              View All
            </Button>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Expand Recent Mistakes" : "Collapse Recent Mistakes"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 30,
              height: 30,
              borderRadius: 8,
              border: `1px solid ${HAIRLINE}`,
              background: "#FAFAFC",
              cursor: "pointer",
            }}
          >
            <WidgetChevron size={15} color={INK} />
          </button>
        </div>
      </div>

      <Collapse in={!collapsed}>
        <div>
          <div style={{ marginTop: 6 }}>
            {state.status === "loading" && (
              <div>
                {Array.from({ length: limit }).map((_, i) => (
                  <React.Fragment key={i}>
                    <SkeletonRow />
                    {i < limit - 1 && <div style={{ borderTop: `1px solid ${HAIRLINE}` }} />}
                  </React.Fragment>
                ))}
              </div>
            )}

            {state.status === "error" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  padding: "26px 12px",
                  color: MUTED,
                  fontSize: 13,
                }}
              >
                <span>Couldn't load your recent mistakes right now.</span>
                <Button
                  onClick={load}
                  size="sm"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: BRAND_SOFT,
                    color: BRAND,
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 12px",
                    fontSize: 12.5,
                    fontWeight: 600,
                  }}
                >
                  <ArrowClockwise size={13} /> Try again
                </Button>
              </div>
            )}

            {state.status === "ready" && state.items.length === 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: "26px 12px",
                  textAlign: "center",
                }}
              >
                <CheckCircleFill size={24} color={CORRECT} />
                <div style={{ fontSize: 13.5, color: INK, fontWeight: 600 }}>You're on top of it!</div>
                <div style={{ fontSize: 12, color: MUTED }}>No wrong answers to review right now.</div>
              </div>
            )}

            {state.status === "ready" &&
              state.items.map((item, i) => {
                const key = item.attemptId + "-" + (item.question?._id || i);
                return (
                  <React.Fragment key={key}>
                    <WrongQuestionRow item={item} isOpen={openRows.has(key)} onToggle={() => toggleRow(key)} isMobileView={isMobileView} subjectMap={subjectsMap} hasIeltsExams={hasIeltsExams} />
                    {i < state.items.length - 1 && <div style={{ borderTop: `1px solid ${HAIRLINE}` }} />}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      </Collapse>
    </div>
  );
}

export default RecentMistakesWidget